import { BalanceData, GearStat, GearTarget } from "../data/balance";
import { GearOwner } from "./gear";

/**
 * Which stats a tower (or the fortress) actually uses, read from the same data the sim reads, so the UI never shows a
 * stat that changes nothing: a Frost tower's damage, a Hex Totem's fire rate. Read-only; the sim does not call this.
 *
 * - Damage (and kill points, which follow the hits) is used when the archetype has a base damage (LevelSim
 *   damageAtTier); a damageless tower uses it only at tier 4, where one of its branches hurts (a Glacier's flat shard
 *   damage, a skill's `auraDps`: Hailstorm, Radiance), and not at all otherwise.
 * - Fire rate only where the tower fires (`fireIntervalTicks` > 0): auras and totems have no shots to speed up.
 * - Range always (shots, auras, a Barracks' post). HP only on the fortress (LevelSim reads the base's gear HP alone).
 */
export enum StatUse { None = 0, Ascended = 1, Full = 2 }

/** Per balance bundle, each tower's damage use (read on every seat score: worth remembering). */
const DAMAGE_USE = new WeakMap<BalanceData, StatUse[]>();

/** Whether a tower's hits (and so its level's damage multiplier and gear damage) do anything, and from which tier. */
export function damageUse(balance: BalanceData, towerIndex: number): StatUse {
  let all = DAMAGE_USE.get(balance);
  if (all === undefined) { all = balance.towers.map((_, i) => computeDamageUse(balance, i)); DAMAGE_USE.set(balance, all); }
  return all[towerIndex] ?? StatUse.None;
}

function computeDamageUse(balance: BalanceData, towerIndex: number): StatUse {
  const a = balance.towers[towerIndex];
  if (a === undefined) return StatUse.None;
  if (!a.damage.isZero()) return StatUse.Full;
  const B = balance.branches;
  const hurts = (B.byTower[towerIndex] ?? []).some((bi) => {
    const b = B.branches[bi];
    return b.tier4.num("flatDamage").isPositive() || b.skills.some((s) => s.params.num("auraDps").isPositive());
  });
  return hurts ? StatUse.Ascended : StatUse.None;
}

/** How a gear stat (an item's primary or an affix) counts on `owner`. */
export function gearStatUse(balance: BalanceData, owner: GearOwner, stat: GearStat): StatUse {
  const isBase = owner.target === GearTarget.Base;
  const i = isBase ? balance.baseTowerIndex : owner.towerIndex;
  const a = balance.towers[i];
  if (a === undefined) return StatUse.None;
  switch (stat) {
    case GearStat.Damage:
    case GearStat.Points: return damageUse(balance, i);
    case GearStat.FireRate: return a.fireIntervalTicks > 0 ? StatUse.Full : StatUse.None;
    case GearStat.Range: return StatUse.Full;
    case GearStat.Hp: return isBase ? StatUse.Full : StatUse.None;
    default: return StatUse.None;
  }
}

/**
 * What a tower's next level (the hub upgrade) changes in battle: its damage multiplier (`tables.levelMult`) on its
 * hits, its soldiers' health (Barracks), its troops (the fortress). The level always raises the gear cap too.
 */
export interface LevelGains {
  readonly damage: StatUse;
  readonly soldierHp: boolean;
  readonly troops: boolean;
}

export function levelGains(balance: BalanceData, towerIndex: number): LevelGains {
  const a = balance.towers[towerIndex];
  return { damage: damageUse(balance, towerIndex), soldierHp: a !== undefined && !a.soldierHp.isZero(), troops: a?.isBase === true };
}
